import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  STRUCTURED_ISSUE_ERROR_CLASSES,
  STRUCTURED_ISSUE_LIST_CLASSES,
  STRUCTURED_ISSUE_PATH_CLASSES,
  STRUCTURED_ISSUE_ROW_CLASSES,
  STRUCTURED_ISSUE_WARNING_CLASSES,
} from '../structured.tokens';
import { type StructuredIssuePath } from '../types/structured-issue.types';

import { type StructuredIssueListProps } from './StructuredIssueList.types';

/** `['gemini','input']` → `gemini.input`; `['list',1]` → `list[1]`. */
function formatPath(path: StructuredIssuePath): string {
  return path
    .map((step, index) =>
      typeof step === 'number' ? `[${String(step)}]` : index === 0 ? step : `.${step}`,
    )
    .join('');
}

/**
 * What is wrong with a structured value, part by part.
 *
 * Rendered as a real list rather than one joined sentence, because the issues
 * come from a schema walk over a nested value and "3 problems" is not something
 * anyone can act on. Each row names the path in the code face — the same face
 * the editor uses — so the operator can find it by eye, and offers to jump to it
 * when the caller knows where it is.
 *
 * Presentation only (U5).
 */
export function StructuredIssueList({
  id,
  title,
  issues,
  offsetOf,
  onGoTo,
}: StructuredIssueListProps) {
  const { t } = useTranslation('uiKit');

  if (issues.length === 0) return null;

  return (
    <div id={id} className={STRUCTURED_ISSUE_LIST_CLASSES}>
      <p className="text-caption font-medium text-foreground">{title}</p>
      <ul aria-label={title} className={STRUCTURED_ISSUE_LIST_CLASSES}>
        {issues.map((issue) => {
          const pathLabel =
            issue.path.length === 0 ? t('structured.rootPath') : formatPath(issue.path);
          const offset = offsetOf?.(issue.path) ?? null;

          return (
            <li
              key={`${formatPath(issue.path)}:${issue.message}`}
              className={STRUCTURED_ISSUE_ROW_CLASSES}
            >
              <span className={STRUCTURED_ISSUE_PATH_CLASSES} dir="ltr">
                {pathLabel}
              </span>
              <span
                className={cn(
                  issue.severity === 'warning'
                    ? STRUCTURED_ISSUE_WARNING_CLASSES
                    : STRUCTURED_ISSUE_ERROR_CLASSES,
                )}
              >
                {issue.message}
              </span>
              {offset !== null && onGoTo !== undefined ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => {
                    onGoTo(offset);
                  }}
                >
                  {t('structured.goToPath', { path: pathLabel })}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
